import SignatoryPersonLink from "../common/SignatoryPersonLink";
import { ownershipPeople, resolvedSignatory, syncLinkedSignatories, linkSignatory, editLinkedPerson } from "../../../utils/linkedScreeningPerson";
import { linkPrefilledSignatories } from "../../../utils/companyReadingPrefill";
import { nameFields } from "../../../utils/individualName";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import Modal from "../../../components/ui/Modal";
import CountrySelect from "../../../components/ui/CountrySelect";
import IndividualNamePreview from "../../../components/clients/IndividualNamePreview";
import { normalizeSignatoryCapacity, SIGNATORY_CAPACITY_OPTIONS } from "../common/signatoryCapacity";
import iconIndividual from "../../../images/avatar.svg";
import iconBo from "../../../images/buildpers.svg";
import iconSignatureMode from "../../../images/affiliate-marketing.svg";
import iconPassport from "../../../images/passport.svg";
import iconAddress from "../../../images/earth-location.svg";
import iconRightArrow from "../../../images/right-arrow.svg";
import "../css/KycClientOwnershipDesktop.css";
import DocumentPrefillMark from "../common/DocumentPrefillMark";
import { isPrefilledRow } from "../../../utils/companyReadingPrefill";

function makeRowId(prefix = "row") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function safeClone(v) {
  try { return structuredClone(v); } catch (_) { return JSON.parse(JSON.stringify(v ?? {})); }
}

const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
function countryLabel(code) {
  if (!code) return "";
  try { return displayNames.of(String(code).toUpperCase()) || code; } catch { return code; }
}

function formatTinyDob(value) {
  if (!value) return "";
  let date = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (typeof value?.seconds === "number") {
    date = new Date(value.seconds * 1000);
  } else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value);
  }
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function InlineDangerMessage({ children, centered = false }) {
  return (
    <div
      className={`kycInlineFieldError ${
        centered ? "kycInlineFieldError--centered" : ""
      }`}
    >
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
      {children}
    </div>
  );
}

function hasCompleteSignatory(row) {
  return (
    String(row?.name || "").trim() &&
    String(row?.capacity || "").trim() &&
    String(row?.nationality || "").trim() &&
    String(row?.countryOfResidence || "").trim()
  );
}

function normalizeSignatureMode(value) {
  if (value === "joint") return "joint";
  if (value === "sole") return "sole";
  return "";
}

const SIGNATURE_MODE_OPTIONS = [
  { value: "sole", label: "Sole" },
  { value: "joint", label: "Joint" },
];

function capacityLabel(options, value) {
  const found = options.find((o) => String(o.value) === String(value || ""));
  return found?.label || "Select";
}

function signatureModeLabel(value) {
  const found = SIGNATURE_MODE_OPTIONS.find((o) => o.value === normalizeSignatureMode(value));
  return found?.label || "Select";
}

function isEmptySignatory(row) {
  return (
    !String(row?.name || "").trim() &&
    !String(row?.capacity || "").trim() &&
    !String(row?.nationality || "").trim() &&
    !String(row?.countryOfResidence || "").trim()
  );
}

function individualKey(row) {
  return [
    String(row?.name || "").trim().toLowerCase().replace(/\s+/g, " "),
    String(row?.nationality || "").trim().toUpperCase(),
    String(row?.countryOfResidence || "").trim().toUpperCase(),
  ].join("|");
}

function collectOwnershipIndividuals(form) {
  return ownershipPeople(form?.ownership).map(person => ({ ...person.row, ...nameFields(person.row), person }));
}

export default function KycClientSignatoriesDesktop({
  sectionNo = 0,
  form,
  onSetAt,
  changedClass,
  changedRowIds = [],
  readOnly = false,
  submitAttempted = false,
  hideHint = false,
  separateNames = false,
}) {
  const signatories = Array.isArray(form?.ownership?.signatories)
    ? form.ownership.signatories
    : [];
  const missingSignatory = submitAttempted && !signatories.some(hasCompleteSignatory);
  // What is missing, by signatory (e.g. "Please complete: Paul (capacity)").
  const incompleteSignatories = signatories
    .filter((row) => String(row?.name || "").trim() && !hasCompleteSignatory(row))
    .map((row) => {
      const missing = [
        !String(row?.capacity || "").trim() ? "capacity" : "",
        !String(row?.nationality || "").trim() ? "nationality" : "",
        !String(row?.countryOfResidence || "").trim() ? "domicile" : "",
      ].filter(Boolean);
      return `${String(row.name).trim()} (${missing.join(", ")})`;
    });
  const missingSignatoryText = incompleteSignatories.length
    ? `Please complete: ${incompleteSignatories.join("; ")}.`
    : "Please add at least one authorised signatory with complete details.";
  const changedRowIdSet = useMemo(
    () => new Set((Array.isArray(changedRowIds) ? changedRowIds : []).map((id) => String(id))),
    [changedRowIds]
  );

  const capacityOptions = useMemo(
    () => [{ value: "", label: "" }].concat(SIGNATORY_CAPACITY_OPTIONS),
    []
  );

  const ownershipIndividuals = useMemo(
    () => collectOwnershipIndividuals(form),
    [form?.ownership] // eslint-disable-line
  );
  const existingSignatoryKeys = useMemo(
    () =>
      new Set(
        signatories
          .filter((row) => String(row?.name || "").trim())
          .map(individualKey)
      ),
    [signatories]
  );
  const availableOwnershipIndividuals = useMemo(
    () =>
      ownershipIndividuals.filter(
        (individual) => !existingSignatoryKeys.has(individualKey(individual))
      ),
    [ownershipIndividuals, existingSignatoryKeys]
  );

  function applyOwnershipIndividual(individual) {
    if (readOnly) return;
    if (existingSignatoryKeys.has(individualKey(individual))) return;
    const list = safeClone(signatories);
    const prefilledRow = {
      _rowId: makeRowId("sig"), name: "", capacity: "", nationality: "", countryOfResidence: "",
      signatureMode: "", jointWithSignatoryIds: [],
    };
    const index = list.length && isEmptySignatory(list[list.length - 1]) ? list.length - 1 : list.length;
    if (index === list.length) list.push(prefilledRow);
    onSetAt?.(["ownership"], linkSignatory({ ...form.ownership, signatories: list }, index, individual.person));
  }

  useEffect(() => {
    if (readOnly) return;
    // A signatory filled from the company documents and a shareholder / controlling person with the same details
    // (e.g. once the guided assistant or the visual builder has generated the ownership) are linked directly.
    const prefilled = form?.customer?.documentPrefill?.signatories;
    if (form?.ownership && prefilled && Object.keys(prefilled).length) {
      const linked = linkPrefilledSignatories(form.ownership, prefilled);
      if (linked !== form.ownership) { onSetAt?.(["ownership"], linked); return; }
    }
    const next = syncLinkedSignatories(form?.ownership);
    if (next?.signatories && JSON.stringify(next.signatories) !== JSON.stringify(signatories)) onSetAt?.(["ownership", "signatories"], next.signatories);
  }, [form?.ownership, readOnly]); // eslint-disable-line

  const signature = useMemo(
    () => signatories.map((r, i) => String(r?._rowId || `missing_${i}`)).join("|"),
    [signatories]
  );

  useEffect(() => {
    if (readOnly) return;
    if (signatories.length === 0) {
      onSetAt?.(["ownership", "signatories"], [{
        _rowId: makeRowId("sig"),
        name: "",
        capacity: "",
        nationality: "",
        countryOfResidence: "",
        signatureMode: "",
        jointWithSignatoryIds: [],
      }]);
      return;
    }
    let changed = false;
    const existingIds = new Set(
      signatories
        .map((r, idx) => String(r?._rowId || `sig_idx_${idx}`))
        .filter(Boolean)
    );
    const next = signatories.map((r) => {
      const row = r && typeof r === "object" ? r : {};
      const normalizedCapacity = normalizeSignatoryCapacity(row?.capacity);
      const rowId = row._rowId || makeRowId("sig");
      const normalizedSignatureMode = normalizeSignatureMode(row?.signatureMode);
      const normalizedJointWith = Array.isArray(row?.jointWithSignatoryIds)
        ? row.jointWithSignatoryIds
            .map((id) => String(id || ""))
            .filter((id) => id && id !== rowId && existingIds.has(id))
        : [];
      if (
        !row._rowId ||
        normalizedCapacity !== String(row?.capacity || "") ||
        normalizedSignatureMode !== String(row?.signatureMode || "") ||
        JSON.stringify(normalizedJointWith) !== JSON.stringify(row?.jointWithSignatoryIds || [])
      ) {
        changed = true;
        return {
          ...row,
          _rowId: rowId,
          capacity: normalizedCapacity,
          signatureMode: normalizedSignatureMode,
          jointWithSignatoryIds: normalizedSignatureMode === "joint" ? normalizedJointWith : [],
        };
      }
      return row;
    });
    if (changed) onSetAt?.(["ownership", "signatories"], next);
  }, [readOnly, signature]); // eslint-disable-line

  function updateRow(idx, key, value) {
    if (readOnly) return;
    const list = safeClone(signatories);
    if (!list[idx]) list[idx] = {};
    if (key === "capacity") {
      list[idx][key] = normalizeSignatoryCapacity(value);
    } else if (key === "signatureMode") {
      list[idx].signatureMode = normalizeSignatureMode(value);
      if (list[idx].signatureMode !== "joint") list[idx].jointWithSignatoryIds = [];
    } else if (key === "jointWithSignatoryIds") {
      const rowId = String(list[idx]?._rowId || "");
      const allowedIds = new Set(
        list
          .map((row, rowIdx) => String(row?._rowId || `sig_idx_${rowIdx}`))
          .filter((id) => id && id !== rowId)
      );
      list[idx].jointWithSignatoryIds = Array.isArray(value)
        ? value.map((id) => String(id || "")).filter((id) => allowedIds.has(id))
        : [];
      list[idx].signatureMode = "joint";
    } else {
      // Name, nationality, domicile: a signatory linked to a shareholder / controlling person is the same
      // person, so the change reaches that row too.
      const patch = typeof key === "object" ? key : { [key]: value };
      onSetAt?.(["ownership"], editLinkedPerson({ ...form.ownership, signatories: list }, "signatories", idx, patch));
      return;
    }
    onSetAt?.(["ownership", "signatories"], list);
  }

  function addRow() {
    if (readOnly) return;
    const list = safeClone(signatories);
    list.push({
      _rowId: makeRowId("sig"),
      name: "",
      capacity: "",
      nationality: "",
      countryOfResidence: "",
      signatureMode: "",
      jointWithSignatoryIds: [],
    });
    onSetAt?.(["ownership", "signatories"], list);
  }

  const shouldPrefillLastCard = signatories.length > 0 && isEmptySignatory(signatories[signatories.length - 1]);

  const [deleting, setDeleting] = useState({});
  const timersRef = useRef({});
  const [openTooltip, setOpenTooltip] = useState(null);
  const [jointPicker, setJointPicker] = useState(null);

  const [openCapacityRowId, setOpenCapacityRowId] = useState(null);
  const [capacityDropdownPos, setCapacityDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const capacityBtnRefs = useRef({});

  const [openSigModeRowId, setOpenSigModeRowId] = useState(null);
  const [sigModeDropdownPos, setSigModeDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const sigModeBtnRefs = useRef({});

  function toggleCapacityDropdown(rowId) {
    if (readOnly) return;
    if (openCapacityRowId === rowId) {
      setOpenCapacityRowId(null);
      return;
    }
    const btn = capacityBtnRefs.current[rowId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setCapacityDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpenCapacityRowId(rowId);
    setOpenSigModeRowId(null);
  }

  function toggleSigModeDropdown(rowId) {
    if (readOnly) return;
    if (openSigModeRowId === rowId) {
      setOpenSigModeRowId(null);
      return;
    }
    const btn = sigModeBtnRefs.current[rowId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setSigModeDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpenSigModeRowId(rowId);
    setOpenCapacityRowId(null);
  }

  useLayoutEffect(() => {
    if (!openCapacityRowId && !openSigModeRowId) return undefined;

    const updateDropdownPositions = () => {
      if (openCapacityRowId) {
        const rect = capacityBtnRefs.current[openCapacityRowId]?.getBoundingClientRect?.();
        if (!rect) setOpenCapacityRowId(null);
        else setCapacityDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
      if (openSigModeRowId) {
        const rect = sigModeBtnRefs.current[openSigModeRowId]?.getBoundingClientRect?.();
        if (!rect) setOpenSigModeRowId(null);
        else setSigModeDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };

    updateDropdownPositions();
    window.addEventListener("scroll", updateDropdownPositions, true);
    window.addEventListener("resize", updateDropdownPositions);
    return () => {
      window.removeEventListener("scroll", updateDropdownPositions, true);
      window.removeEventListener("resize", updateDropdownPositions);
    };
  }, [openCapacityRowId, openSigModeRowId]);

  const jointPickerRow =
    jointPicker && Number.isInteger(jointPicker.idx) ? signatories[jointPicker.idx] : null;
  const jointPickerRowId = jointPickerRow
    ? String(jointPickerRow?._rowId || `sig_idx_${jointPicker.idx}`)
    : "";
  const jointPickerOptions = jointPicker
    ? signatories
        .map((row, rowIdx) => ({
          id: String(row?._rowId || `sig_idx_${rowIdx}`),
          name: String(row?.name || "").trim() || `Signatory ${rowIdx + 1}`,
        }))
        .filter((option) => option.id && option.id !== jointPickerRowId)
    : [];
  const selectedJointIds = Array.isArray(jointPickerRow?.jointWithSignatoryIds)
    ? jointPickerRow.jointWithSignatoryIds.map((id) => String(id))
    : [];

  function handleSignatureModeChange(idx, value) {
    updateRow(idx, "signatureMode", value);
    if (value === "joint") {
      setJointPicker({ idx });
    } else {
      setJointPicker(null);
    }
  }

  function toggleJointSignatory(id) {
    if (!jointPicker || !id) return;
    const current = new Set(selectedJointIds);
    if (current.has(id)) current.delete(id);
    else current.add(id);
    updateRow(jointPicker.idx, "jointWithSignatoryIds", Array.from(current));
  }

  function handleCountryMouseEnter(tooltipId, el) {
    const span = el?.querySelector(".rs__single-value span");
    if (span && span.scrollWidth > span.clientWidth) {
      setOpenTooltip(tooltipId);
    }
  }

  function handleCountryMouseLeave() {
    setOpenTooltip(null);
  }

  function requestDelete(rowId) {
    if (readOnly || !rowId) return;
    setDeleting((p) => ({ ...p, [rowId]: true }));
    const key = String(rowId);
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
    timersRef.current[key] = setTimeout(() => {
      const next = safeClone(signatories)
        .filter((r) => String(r?._rowId || "") !== key)
        .map((row) => ({
          ...row,
          jointWithSignatoryIds: Array.isArray(row?.jointWithSignatoryIds)
            ? row.jointWithSignatoryIds.filter((id) => String(id) !== key)
            : [],
        }));
      onSetAt?.(["ownership", "signatories"], next);
      setDeleting((p) => { const n = { ...p }; delete n[key]; return n; });
      delete timersRef.current[key];
    }, 160);
  }

  useEffect(() => () => Object.values(timersRef.current).forEach(clearTimeout), []);

  return (
    <div className="card kycCard mb-3">
      <div className="kycSectionHead">
        <h5 className="kycSectionTitle mb-0">{sectionNo} | Authorised Signatories</h5>
        {!hideHint && (
          <div className="kycSectionHint">
            Please add the individuals who are legally authorised to act or sign on behalf of the company
          </div>
        )}
      </div>

      <div className="kycSectionBody">
        {signatories.length === 0 && readOnly ? (
          <div style={{ color: "#9aa5b0", fontSize: 14 }}>—</div>
        ) : null}

        <div className="d-grid" style={{ gap: 12 }}>
          {signatories.map((storedRow, idx) => {
            const r = resolvedSignatory(form.ownership, storedRow);
            const rowId = r?._rowId || `sig_idx_${idx}`;
            const isDeleting = !!deleting?.[rowId];
            const isChangedRow =
              changedRowIdSet.has(String(rowId)) ||
              changedRowIdSet.has(`idx_${idx}`);
            const dobLabel = formatTinyDob(r?.dateOfBirth || r?.dob || r?.dobOrIncorporationDate);
            // Joint signature: names of the signatories this one signs with (tooltip on hover).
            const jointNames = normalizeSignatureMode(r?.signatureMode) === "joint"
              ? (storedRow?.jointWithSignatoryIds || []).map((id) => {
                  const otherIdx = signatories.findIndex((row, i) => String(row?._rowId || `sig_idx_${i}`) === String(id));
                  if (otherIdx < 0) return "";
                  const other = resolvedSignatory(form.ownership, signatories[otherIdx]);
                  return String(other?.name || "").trim() || `Signatory ${otherIdx + 1}`;
                }).filter(Boolean)
              : null;
            return (
              <div
                key={rowId}
                className={
                  "kycOwnershipPartyCard kycSignatoriesCard kycSigCardWrap" +
                  (isChangedRow ? " kycChangedWrap" : "")
                }
                style={isDeleting ? { opacity: 0.4, pointerEvents: "none" } : undefined}
              >
                {isChangedRow ? (
                  <img
                    src={iconRightArrow}
                    className="kycChangedRowArrow kycChangedRowArrow--left"
                    alt=""
                    aria-hidden="true"
                  />
                ) : null}
                {dobLabel ? (
                  <div className="kycPartyTinyDob">DOB: {dobLabel}</div>
                ) : null}

                {!readOnly ? (
                  <button
                    type="button"
                    className="kycSigDeleteBtn"
                    onClick={() => requestDelete(rowId)}
                    aria-label="Remove signatory"
                  >
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                ) : null}

                {/* Name, capacity, signature mode, nationality, domicile */}
                <div className="kycSigEditGrid4 kycSigEditGrid5">
                  {/* Full name */}
                  <div className="kycInfoItem kycInfoItemAlignTop">
                    <div className="kycInfoIconDarkBeige">
                      <img src={iconIndividual} alt="" className="kycInfoIconImg" />
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycInfoViewLabel">Signatory's name<DocumentPrefillMark show={isPrefilledRow(form?.customer, "signatories", r)} /></div>
                      {separateNames && !readOnly ? (
                        <IndividualNamePreview
                          inputClass="form-control kycCustomerDetailsHybridInput--line"
                          displayName={r?.name || ""}
                            value={r} onChange={(patch) => updateRow(idx, patch)}
                          disabled={isDeleting}
                        />
                      ) : (
                      <input
                        className="form-control kycCustomerDetailsHybridInput--line"
                        value={r?.name || ""}
                        disabled={readOnly || isDeleting}
                        onChange={(e) => updateRow(idx, "name", e.target.value)}
                      />
                      )}
                    </div>
                  </div>

                  {/* Capacity */}
                  <div className="kycInfoItem kycInfoItemAlignTop">
                    <div className="kycInfoIconLightGrey">
                      <img src={iconBo} alt="" className="kycInfoIconImg" />
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycInfoViewLabel">Capacity</div>
                      <button
                        ref={(el) => { capacityBtnRefs.current[rowId] = el; }}
                        type="button"
                        className="kycShCapacityBtn kycCustomerDetailsHybridInput--line kycSigSelectBtn"
                        disabled={readOnly || isDeleting}
                        onClick={() => toggleCapacityDropdown(rowId)}
                      >
                        <span
                          className={
                            "kycShCapacityBtnText" +
                            (r?.capacity ? " kycShCapacityBtnText--filled" : "") +
                            (submitAttempted && !r?.capacity && !isEmptySignatory(r) ? " kycSigFieldMissing" : "")
                          }
                        >
                          {capacityLabel(capacityOptions, r?.capacity)}
                        </span>
                        <span className="kycShTypeArrow" aria-hidden="true">▾</span>
                      </button>

                      {!readOnly &&
                        openCapacityRowId === rowId &&
                        createPortal(
                          <>
                            <div
                              className="kycShTypeDismiss"
                              onClick={() => setOpenCapacityRowId(null)}
                            />
                            <div
                              className="kycShTypeDropdown kycShCapacityDropdown"
                              style={{
                                position: "fixed",
                                top: capacityDropdownPos.top,
                                left: capacityDropdownPos.left,
                                minWidth: capacityDropdownPos.width || 170,
                              }}
                            >
                              {capacityOptions.filter((o) => o.value).map((o) => (
                                <button
                                  key={o.value}
                                  type="button"
                                  className={
                                    "kycShTypeOption" +
                                    (String(r?.capacity || "") === String(o.value) ? " is-active" : "")
                                  }
                                  onClick={() => {
                                    updateRow(idx, "capacity", o.value);
                                    setOpenCapacityRowId(null);
                                  }}
                                >
                                  {o.label}
                                </button>
                              ))}
                            </div>
                          </>,
                          document.body
                        )}
                    </div>
                  </div>

                  {/* Signature mode (sole / joint) */}
                  <div className="kycInfoItem kycInfoItemAlignTop">
                    <div className="kycInfoIconLightGrey">
                      <img src={iconSignatureMode} alt="" className="kycInfoIconImg" />
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycInfoViewLabel">Signature mode</div>
                      {/* Wrapper carries the tooltip: a disabled button (read-only) gets no hover events. */}
                      <span
                        className="kycSigJointAnchor"
                        data-tooltip-id={jointNames != null ? `sig-joint-${rowId}` : undefined}
                        data-tooltip-content={jointNames != null ? (jointNames.length ? `Joint with ${jointNames.join(", ")}` : "No joint signatory selected") : undefined}
                      >
                      <button
                        ref={(el) => { sigModeBtnRefs.current[rowId] = el; }}
                        type="button"
                        className="kycShCapacityBtn kycCustomerDetailsHybridInput--line kycSigSelectBtn"
                        disabled={readOnly || isDeleting}
                        onClick={() => toggleSigModeDropdown(rowId)}
                      >
                        <span
                          className={
                            "kycShCapacityBtnText" +
                            (normalizeSignatureMode(r?.signatureMode) ? " kycShCapacityBtnText--filled" : "")
                          }
                        >
                          {signatureModeLabel(r?.signatureMode)}
                        </span>
                        <span className="kycShTypeArrow" aria-hidden="true">▾</span>
                      </button>
                      </span>
                      {jointNames != null && openSigModeRowId !== rowId ? (
                        <Tooltip id={`sig-joint-${rowId}`} place="bottom" style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} />
                      ) : null}

                      {!readOnly &&
                        openSigModeRowId === rowId &&
                        createPortal(
                          <>
                            <div
                              className="kycShTypeDismiss"
                              onClick={() => setOpenSigModeRowId(null)}
                            />
                            <div
                              className="kycShTypeDropdown kycShCapacityDropdown"
                              style={{
                                position: "fixed",
                                top: sigModeDropdownPos.top,
                                left: sigModeDropdownPos.left,
                                minWidth: sigModeDropdownPos.width || 170,
                              }}
                            >
                              {SIGNATURE_MODE_OPTIONS.map((o) => (
                                <button
                                  key={o.value}
                                  type="button"
                                  className={
                                    "kycShTypeOption" +
                                    (normalizeSignatureMode(r?.signatureMode) === o.value ? " is-active" : "")
                                  }
                                  onClick={() => {
                                    handleSignatureModeChange(idx, o.value);
                                    setOpenSigModeRowId(null);
                                  }}
                                >
                                  {o.label}
                                </button>
                              ))}
                            </div>
                          </>,
                          document.body
                        )}
                    </div>
                  </div>

                  {/* Nationality */}
                  <div className="kycInfoItem kycInfoItemAlignTop">
                    <div className="kycInfoIconLightGrey">
                      <img src={iconPassport} alt="" className="kycInfoIconImg" />
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycInfoViewLabel">Nationality</div>
                      <div
                        data-tooltip-id={`sig-nat-${rowId}`}
                        data-tooltip-content={countryLabel(r?.nationality)}
                        onMouseEnter={(e) => handleCountryMouseEnter(`sig-nat-${rowId}`, e.currentTarget)}
                        onMouseLeave={handleCountryMouseLeave}
                      >
                        <CountrySelect
                          value={r?.nationality || ""}
                          onChange={(val) => updateRow(idx, "nationality", val || "")}
                          disabled={readOnly || isDeleting}
                          className="kycCustomerDetailsHybridCountrySelect"
                          singleValueMode="iso3"
                          optionFontSize={12}
                          placeholder=""
                        />
                      </div>
                      {r?.nationality ? <Tooltip id={`sig-nat-${rowId}`} place="bottom" isOpen={openTooltip === `sig-nat-${rowId}`} style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} /> : null}
                    </div>
                  </div>

                  {/* Domicile */}
                  <div className="kycInfoItem kycInfoItemAlignTop">
                    <div className="kycInfoIconLightGrey">
                      <img src={iconAddress} alt="" className="kycInfoIconImg" />
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycInfoViewLabel">Domicile</div>
                      <div
                        data-tooltip-id={`sig-dom-${rowId}`}
                        data-tooltip-content={countryLabel(r?.countryOfResidence)}
                        onMouseEnter={(e) => handleCountryMouseEnter(`sig-dom-${rowId}`, e.currentTarget)}
                        onMouseLeave={handleCountryMouseLeave}
                      >
                        <CountrySelect
                          value={r?.countryOfResidence || ""}
                          onChange={(val) => updateRow(idx, "countryOfResidence", val || "")}
                          disabled={readOnly || isDeleting}
                          className="kycCustomerDetailsHybridCountrySelect"
                          singleValueMode="iso3"
                          optionFontSize={12}
                          placeholder=""
                        />
                      </div>
                      {r?.countryOfResidence ? <Tooltip id={`sig-dom-${rowId}`} place="bottom" isOpen={openTooltip === `sig-dom-${rowId}`} style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} /> : null}
                    </div>
                  </div>
                </div>
                <SignatoryPersonLink ownership={form.ownership} index={idx} readOnly={readOnly || isDeleting}
                  onChange={ownership => onSetAt?.(["ownership"], ownership)} />
              </div>
            );
          })}
        </div>

        {missingSignatory ? (
          <div style={{ marginTop: 10 }}>
            <InlineDangerMessage centered>
              {missingSignatoryText}
            </InlineDangerMessage>
          </div>
        ) : null}

        {!readOnly ? (
          <div className="kycSigFooterRow" style={signatories.length > 0 ? { marginTop: 16 } : undefined}>
            <button
              type="button"
              className="kycSigAddBtn"
              onClick={addRow}
            >
              <i className="bi bi-plus-lg me-2" aria-hidden="true" />
              Add signatory
            </button>

            {availableOwnershipIndividuals.length > 0 ? (
              <div className="kycSigSuggestionsWrap">
                <span className="kycSigSuggestionsLabel">
                  {shouldPrefillLastCard
                    ? "Click to prefill from"
                    : "Click to add prefilled individual"}
                </span>
                <div className="kycSigSuggestions">
                  {availableOwnershipIndividuals.map((ind) => (
                    <button
                      key={ind.person.ref}
                      type="button"
                      className="kycSigSuggestionBadge"
                      onClick={() => applyOwnershipIndividual(ind)}
                      title={
                        shouldPrefillLastCard
                          ? `Fill last signatory card with ${ind.name}`
                          : `Add ${ind.name} as a new signatory`
                      }
                    >
                      {ind.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <Modal
        open={!!jointPicker}
        title="Joint signatories"
        onClose={() => setJointPicker(null)}
        className="kycSignatoryJointModal"
        footer={
          <>
            <button
              type="button"
              className="btn btn-modal-cancel"
              onClick={() => setJointPicker(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-modal-save"
              onClick={() => setJointPicker(null)}
            >
              Done
            </button>
          </>
        }
      >
        {jointPickerOptions.length ? (
          <div className="kycSigJointModalList">
            {jointPickerOptions.map((option) => (
              <label key={option.id} className="kycSigJointModalOption">
                <input
                  type="checkbox"
                  checked={selectedJointIds.includes(option.id)}
                  onChange={() => toggleJointSignatory(option.id)}
                />
                <span>{option.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="kycSigJointModalEmpty">
            Add another signatory first.
          </div>
        )}
      </Modal>
    </div>
  );
}
